import {
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Stack,
    Text,
    Select,
    Checkbox,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from 'react-hook-form';
import { useNavigate } from "react-router-dom";

const cardsBorderRadii ="8px";

type Props = {
    initialData?: any;
    onSubmit: (data: any) => void;
};

const extractFlightDetails = (flight: any) => {
    const segment = flight.itineraries?.[0]?.segments?.[0] || {};
    const travelerPricing = flight.travelerPricings?.[0] || {};
    const fareDetail = travelerPricing.fareDetailsBySegment?.[0] || {};

    return {
        airline: flight.validatingAirlineCodes?.join(", ") || "",
        flightNumber: `${segment.carrierCode || ""}${segment.number || ""}`,
        departureAirport: segment.departure?.iataCode || "",
        arrivalAirport: segment.arrival?.iataCode || "",
        departureTime: segment.departure?.at || "",
        arrivalTime: segment.arrival?.at || "",
        duration: segment.duration || flight.itineraries?.[0]?.duration || "",
        cabinClass: fareDetail.cabin || "",
        currency: flight.price?.currency || "",
        totalPrice: flight.price?.grandTotal || "",
        bags: fareDetail.includedCheckedBags?.weight
            ? `${fareDetail.includedCheckedBags.weight} ${fareDetail.includedCheckedBags.weightUnit}`
            : "",
    };
};

const mockBagPrice = 20; // €20 for extra bag
const seatOptions = ["A1", "A2", "B1", "B2", "C1", "C2"];

const FlightBookingForm = ({ initialData, onSubmit }: Props) => {
    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm();

    const navigate = useNavigate();

    const roundTripData = sessionStorage.getItem("selectedFlightRoundTrip");
    const isRoundTrip = roundTripData !== null;

    const departureFlight = isRoundTrip ? JSON.parse(roundTripData!).departure : initialData;
    const returnFlight = isRoundTrip ? JSON.parse(roundTripData!).return : null;
    const totalPassengers = isRoundTrip
        ? JSON.parse(roundTripData!).totalPassengers
        : initialData?.totalPassengers || 1;

    const baseFare = parseFloat(
        isRoundTrip
            ? JSON.parse(roundTripData!).total
            : initialData?.price?.grandTotal || "0"
    );

    const [extraBag, setExtraBag] = useState(false);
    const [selectedSeats, setSelectedSeats] = useState<string[]>([]);

    const [computedTotal, setComputedTotal] = useState(baseFare);

    useEffect(() => {
        const total = baseFare + (extraBag ? mockBagPrice : 0);
        setComputedTotal(total);
    }, [extraBag, baseFare]);

    useEffect(() => {
        if (initialData) {
            const { airline, flightNumber, departureAirport, arrivalAirport, departureTime, arrivalTime, duration, cabinClass, currency, totalPrice, bags } =
                !isRoundTrip
                    ? extractFlightDetails(initialData)
                    : {
                        airline: "",
                        flightNumber: "",
                        departureAirport: "",
                        arrivalAirport: "",
                        departureTime: "",
                        arrivalTime: "",
                        duration: "",
                        cabinClass: "",
                        currency: initialData.total?.currency || "EUR",
                        totalPrice: initialData.total,
                        bags: "",
                    };

            reset({
                passengerName: "",
                email: "",
                mobile: "",
                numberOfPassengers: totalPassengers,
                airline,
                flightNumber,
                departureAirport,
                arrivalAirport,
                departureTime,
                arrivalTime,
                duration,
                cabinClass,
                currency,
                totalPrice,
                bags,
            });
        }
    }, [initialData, reset, isRoundTrip]);

    const handleSeatSelect = (event: React.ChangeEvent<HTMLSelectElement>, index: number) => {
        const newSeats = [...selectedSeats];
        newSeats[index] = event.target.value;
        setSelectedSeats(newSeats);
    };

    return (
        <Box as="form" onSubmit={handleSubmit(onSubmit)} p={5} borderWidth={1} borderRadius={cardsBorderRadii}>
            <Text fontSize="xl" mb={4}>Flight Booking</Text>

            <Stack spacing={4}>
                <FormControl isRequired>
                    <FormLabel>Passenger Name</FormLabel>
                    <Input {...register("passengerName", { required: true })} />
                </FormControl>

                <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input type="email" {...register("email", { required: true })} />
                </FormControl>

                <FormControl isRequired>
                    <FormLabel>Mobile</FormLabel>
                    <Input {...register("mobile", { required: true })} />
                </FormControl>

                <FormControl isRequired>
                    <FormLabel>Number of Passengers</FormLabel>
                    <Input type="number" {...register("numberOfPassengers")} />
                </FormControl>

                <Flex width={'100%'} gap={'30px'} align="flex-start" flexWrap="wrap">
                    {/* Departure */}
                    <Box width="100%" maxW="400px">
                        <Text fontWeight="bold" mb={2}>Departure Flight Details</Text>
                        {Object.entries(extractFlightDetails(departureFlight)).map(([label, value]) => (
                            <FormControl key={label}>
                                <FormLabel>{label.charAt(0).toUpperCase() + label.slice(1)}</FormLabel>
                                <Input value={value} isReadOnly />
                            </FormControl>
                        ))}
                    </Box>

                    {/* Return */}
                    {returnFlight && (
                        <Box width="100%" maxW="400px">
                            <Text fontWeight="bold" mb={2}>Return Flight Details</Text>
                            {Object.entries(extractFlightDetails(returnFlight)).map(([label, value]) => (
                                <FormControl key={label}>
                                    <FormLabel>{label.charAt(0).toUpperCase() + label.slice(1)}</FormLabel>
                                    <Input value={value} isReadOnly />
                                </FormControl>
                            ))}
                        </Box>
                    )}

                    {/* Optional fields */}
                    <Box>
                        <FormControl>
                            <FormLabel>Extra Checked Bag (€{mockBagPrice})</FormLabel>
                            <Checkbox isChecked={extraBag} onChange={() => setExtraBag(!extraBag)}>
                                Add Bag
                            </Checkbox>
                        </FormControl>

                        <FormControl mt={4}>
                            <FormLabel>Select Seat{isRoundTrip ? "s" : ""} (Optional)</FormLabel>
                            {[...Array(isRoundTrip ? 2 : 1)].map((_, idx) => (
                                <Select
                                    key={idx}
                                    placeholder={`Select Seat ${idx + 1}`}
                                    onChange={(e) => handleSeatSelect(e, idx)}
                                    mb={2}
                                >
                                    {seatOptions.map((seat) => (
                                        <option key={seat} value={seat}>{seat}</option>
                                    ))}
                                </Select>
                            ))}
                        </FormControl>

                        <FormControl mt={4}>
                            <FormLabel>Grand Total (Incl. bags x passengers)</FormLabel>
                            <Input
                                value={`${(computedTotal * totalPassengers).toFixed(2)} ${initialData?.price?.currency || "EUR"}`}
                                isReadOnly
                            />
                        </FormControl>
                        <Button onClick={() => navigate('/flights/checkout')} colorScheme="blue" type="submit">
                            Confirm & Checkout
                        </Button>
                    </Box>
                </Flex>
            </Stack>
        </Box>
    );
};

export default FlightBookingForm;
